import { Outlet, useNavigate, NavLink } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useAuthStore, logout as cognitoLogout } from '@ipsoftsrl/frontend-auth';
import { api } from '../../services/api-client';
import { ToastContainer } from '../ui/Toast';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import { MandatoryDocsModal } from '../MandatoryDocsModal';

/**
 * Main application layout with navigation header.
 */
export function AppLayout() {
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isAdmin = user?.groups?.includes('Administrators');
  const isOperator = user?.groups?.includes('Operators') || isAdmin;

  // Send login context (IP, device, geo) once after login
  const loginContextSent = useRef(false);
  useEffect(() => {
    if (!loginContextSent.current) {
      loginContextSent.current = true;
      api.post('/auth/login-context', {
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
      }).catch(() => {});
    }
  }, []);

  const handleLogout = () => {
    // Record logout in audit log with device context
    api.post('/auth/logout', {
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
    }).catch(() => {});
    cognitoLogout();
    clearAuth();
    navigate('/login', { replace: true });
  };

  return (
    <div className="app-layout">
      <header className="app-header" role="banner">
        <div className="header-brand">
          <h1>Portali Online</h1>
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
        <nav className={`header-nav ${mobileMenuOpen ? 'header-nav--open' : ''}`} aria-label="Navigazione principale">
          <NavLink to="/dashboard" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
            Dashboard
          </NavLink>
          <NavLink to="/portals/documentale" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
            Documenti
          </NavLink>
          <NavLink to="/portals/documentale/mandatory" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
            Obbligatori
          </NavLink>
          {isOperator && (
            <>
              <NavLink to="/admin/users" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                Utenti
              </NavLink>
              <NavLink to="/admin/census" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                Censimento
              </NavLink>
            </>
          )}
          {isAdmin && (
            <NavLink to="/admin/portals" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
              Portali
            </NavLink>
          )}
          <div className="nav-dropdown">
            <button className="nav-link nav-dropdown-trigger">Intranet ▾</button>
            <div className="nav-dropdown-menu">
              <NavLink to="/portals/intranet" className={({ isActive }) => isActive ? 'nav-dropdown-item active' : 'nav-dropdown-item'}>
                Aree
              </NavLink>
              {isAdmin && (
                <NavLink to="/admin/intranet" className={({ isActive }) => isActive ? 'nav-dropdown-item active' : 'nav-dropdown-item'}>
                  Gestione
                </NavLink>
              )}
            </div>
          </div>
          {isAdmin && (
            <div className="nav-dropdown">
              <button className="nav-link nav-dropdown-trigger">Log ▾</button>
              <div className="nav-dropdown-menu">
                <NavLink to="/admin/intranet/audit" className={({ isActive }) => isActive ? 'nav-dropdown-item active' : 'nav-dropdown-item'}>
                  Audit Intranet
                </NavLink>
                <NavLink to="/admin/logs/activity" className={({ isActive }) => isActive ? 'nav-dropdown-item active' : 'nav-dropdown-item'}>
                  Log attività utente
                </NavLink>
                <NavLink to="/admin/logs/documents" className={({ isActive }) => isActive ? 'nav-dropdown-item active' : 'nav-dropdown-item'}>
                  Log documenti
                </NavLink>
              </div>
            </div>
          )}
        </nav>
        <div className="header-user">
          {user && (
            <span className="user-name">
              {user.givenName} {user.familyName}
            </span>
          )}
          <button onClick={handleLogout} className="btn btn-ghost btn-sm" aria-label="Disconnetti">
            Esci
          </button>
        </div>
      </header>
      <main className="app-main" role="main">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
      <ToastContainer />
      <MandatoryDocsModal />
    </div>
  );
}
